// src/pages/orders/adminProductDetailPage.js — product detail (catalog CMS).
//
// Cloned from the Saleor product page: General Information, a Media manager, and
// the Variants matrix with an Available checkbox + Price PER CHANNEL, plus an
// Availability rail. Sections Saleor shows that the engine has no data for
// (Category, Collections, Tax class, SEO, Rating, Cost price, Weight) are omitted
// rather than stubbed — a dead input box is a promise the system cannot keep.
//
// The matrix is a plain DOM table. Saleor renders it on <canvas> with
// glide-data-grid to virtualise thousands of rows; Barsys has six products with at
// most three variants, where a table is accessible and keyboard-navigable for free.
//
// REQUIRES ENGINE v41.
import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardBody, CardHeader } from "../../components/card/card.jsx";
import {
  fetchAdminProduct, saveProductForm, uploadProductMedia,
  reorderProductMedia, deleteProductMedia, setVariantMedia,
} from "../../actions/commerce_admin_actions";
import { parseDoc, toFields, withTexts, serialize, appendParagraph, removeBlock } from "./editorJsDoc";
import { fetchTaxonomy, setProductCategory, fetchProductCategory } from "../../actions/taxonomy_actions";

// Prices are shown as RAW numbers in the matrix with the currency as a separate
// label, not run through Intl currency formatting. These are edit fields: a
// formatted "$69.99" has to be un-formatted before it can be parsed back, and
// grouping separators differ by locale, so the round-trip loses to the first
// operator on a comma-decimal machine.

// Descriptions imported from Saleor are EditorJS documents stored as a JSON string,
// and the storefront parses that structure to render the PDP. They were read-only
// here because a plain textarea over the JSON destroys it the moment anyone types —
// which made product copy the last thing that still required Saleor.
//
// Now editable per BLOCK, via ./editorJsDoc, which rewrites only data.text and puts
// ids, types, extra data keys and unknown top-level fields back untouched. Inline
// HTML is shown raw: five blocks across the catalogue carry <b>, and a WYSIWYG that
// silently dropped it would quietly flatten the storefront's typography.

// Per-block description editor. Blocks with no editable text (an image, an embed)
// are listed but not offered as inputs — an empty textarea over an image block would
// invite someone to type into nothing.
function DescriptionEditor({ doc, onChange }) {
  const fields = toFields(doc);
  const setText = (i, v) => onChange(withTexts(doc, { [i]: v }));
  return (
    <div>
      {fields.map((f) => (
        <div key={`${f.id || 'b'}-${f.index}`} className="mb-2">
          <div className="d-flex align-items-center mb-1">
            <span className="small text-inverse text-opacity-50">
              {f.type}{f.editable ? "" : " (not editable here)"}
            </span>
            {fields.length > 1 ? (
              <button type="button" className="btn btn-link btn-sm p-0 ms-auto text-danger"
                onClick={() => onChange(removeBlock(doc, f.index))}>remove</button>
            ) : null}
          </div>
          {f.editable ? (
            <textarea className="form-control form-control-sm" rows={Math.min(6, Math.max(2, Math.ceil(f.text.length / 90)))}
              value={f.text} onChange={(e) => setText(f.index, e.target.value)} />
          ) : (
            <div className="small text-inverse text-opacity-25 border rounded p-2">
              passed through unchanged
            </div>
          )}
        </div>
      ))}
      <button type="button" className="btn btn-sm btn-outline-theme" onClick={() => onChange(appendParagraph(doc))}>
        + paragraph
      </button>
      <div className="small text-inverse text-opacity-50 mt-2">
        Inline HTML such as &lt;b&gt; is shown as-is and kept. An emptied paragraph is removed on save.
      </div>
    </div>
  );
}

// The category selector.
//
// Saved on change rather than with the page's Save button, and deliberately: category
// is a single scalar behind its own mutation (adminProductCategorySet), not part of
// adminProductUpsert. Folding it into the form draft would mean a Save that half
// succeeds — product fields written, category not — with one banner covering both.
function CategoryPicker({ productId }) {
  const [categories, setCategories] = React.useState(null);
  const [current, setCurrent] = React.useState(null);
  const [value, setValue] = React.useState("");
  const [unsupported, setUnsupported] = React.useState(false);
  const [result, setResult] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    // Both reads are separate from the page's product query on purpose — see
    // fetchProductCategory. Neither can take the page down.
    Promise.all([fetchTaxonomy(), fetchProductCategory(productId)]).then(([t, c]) => {
      if (!alive) return;
      setCategories(t.categories);
      if (c.unsupported) setUnsupported(true);
      else { setCurrent(c.category || ""); setValue(c.category || ""); }
    });
    return () => { alive = false; };
  }, [productId]);

  const change = async (slug) => {
    setValue(slug); setResult(null);
    const r = await setProductCategory(productId, slug || null);
    setResult(r.ok ? { ok: true } : r);
    if (r.ok) setCurrent(slug);
    else setValue(current || "");   // put the control back where it was
  };

  if (unsupported) {
    return (
      <div className="small text-inverse text-opacity-50">
        Categories need engine v51 — this engine does not have the field yet.
      </div>
    );
  }
  if (!categories) return <div className="small text-inverse text-opacity-50">Loading categories…</div>;
  if (!categories.length) {
    return (
      <div className="small text-inverse text-opacity-50">
        No categories yet — create one under Categories &amp; Collections.
      </div>
    );
  }
  return (
    <div>
      <select className="form-select" value={value} onChange={(e) => change(e.target.value)}>
        <option value="">— no category —</option>
        {categories.map((c) => (
          <option key={c.id} value={c.slug}>{"— ".repeat(c.level)}{c.name}</option>
        ))}
      </select>
      <div className={`small mt-1 ${result && !result.ok ? "text-danger" : "text-inverse text-opacity-50"}`}>
        {result && !result.ok ? result.message : "Saved as soon as you pick one."}
      </div>
    </div>
  );
}

// ---------------------------------------------------------- PDP media order box
// Shows product media in the order currently displayed on barsys.com (engine
// sort_order). Drag to change that order — saves immediately via reorderProductMedia.

function PDPMediaOrder({ product }) {
  const dispatch = useDispatch();
  const [dragId, setDragId] = useState(null);
  const [order, setOrder] = useState(product.media || []);

  // Sync when product reloads after a save
  React.useEffect(() => { setOrder(product.media || []); }, [product.media]);

  const onDrop = (targetId) => {
    if (dragId == null || dragId === targetId) return;
    const ids = order.map((m) => m.id);
    const from = ids.indexOf(dragId), to = ids.indexOf(targetId);
    if (from < 0 || to < 0) return;
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    setOrder(ids.map((id) => order.find((m) => m.id === id)));
    setDragId(null);
    dispatch(reorderProductMedia(product.id, ids));
  };

  if (!order.length) return null;

  return (
    <Card className="mb-3">
      <CardHeader className="d-flex justify-content-between align-items-center">
        <span>
          PDP Display Order
          <span className="text-inverse text-opacity-50 small ms-2">drag to set the order shown on barsys.com</span>
        </span>
        <span className="badge bg-theme">live on site</span>
      </CardHeader>
      <CardBody>
        <div className="d-flex flex-wrap gap-2">
          {order.map((m, i) => (
            <div
              key={m.id}
              draggable
              onDragStart={() => setDragId(m.id)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(m.id)}
              title={`${m.url}\nDrag to reorder`}
              style={{ position: "relative", cursor: "grab", opacity: dragId === m.id ? 0.4 : 1 }}>
              {m.type === "VIDEO"
                ? <video src={m.url} width="96" height="96" muted style={{ objectFit: "cover", borderRadius: 8, border: "2px solid rgba(255,255,255,.2)" }} />
                : <img src={m.url} alt={m.alt || ""} width="96" height="96" style={{ objectFit: "cover", borderRadius: 8, border: "2px solid rgba(255,255,255,.2)" }} />}
              <span className="badge bg-secondary" style={{ position: "absolute", top: 4, left: 4, fontSize: 10 }}>{i + 1}</span>
            </div>
          ))}
        </div>
        <div className="small text-inverse text-opacity-50 mt-2">
          Position 1 is the first image shown on the product page. Changes save immediately.
        </div>
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------- media manager

function MediaManager({ product }) {
  const dispatch = useDispatch();
  const [dragId, setDragId] = useState(null);
  const media = product.media || [];

  const onFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) dispatch(uploadProductMedia(product.id, files));
    e.target.value = ""; // let the same file be picked again after a failure
  };

  // Drop reorders locally then sends the FULL id list, because the engine requires
  // a permutation — a partial list is refused so a stale page cannot scatter an
  // image someone else just uploaded.
  const onDrop = (targetId) => {
    if (dragId == null || dragId === targetId) return;
    const ids = media.map((m) => m.id);
    const from = ids.indexOf(dragId), to = ids.indexOf(targetId);
    if (from < 0 || to < 0) return;
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    setDragId(null);
    dispatch(reorderProductMedia(product.id, ids));
  };

  return (
    <Card className="mb-3">
      <CardHeader className="d-flex justify-content-between align-items-center">
        <span>Media <span className="text-inverse text-opacity-50 small">({media.length})</span></span>
        <label className="btn btn-outline-theme btn-sm mb-0">
          Upload
          <input type="file" accept="image/*,video/*" multiple onChange={onFiles} style={{ display: "none" }} />
        </label>
      </CardHeader>
      <CardBody>
        {media.length === 0 ? (
          <div className="text-inverse text-opacity-50 small">No media yet. Upload images or video for this product.</div>
        ) : (
          <div className="d-flex flex-wrap gap-2">
            {media.map((m, i) => (
              <div key={m.id}
                draggable
                onDragStart={() => setDragId(m.id)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDrop(m.id)}
                title={`${m.url}\nDrag to reorder`}
                style={{ position: "relative", cursor: "grab", opacity: dragId === m.id ? 0.4 : 1 }}>
                {m.type === "VIDEO"
                  ? <video src={m.url} width="96" height="96" muted style={{ objectFit: "cover", borderRadius: 8, border: "1px solid rgba(255,255,255,.15)" }} />
                  : <img src={m.url} alt={m.alt || ""} width="96" height="96" style={{ objectFit: "cover", borderRadius: 8, border: "1px solid rgba(255,255,255,.15)" }} />}
                {/* The first image is the thumbnail everywhere else, so say so. */}
                {i === 0 ? <span className="badge bg-theme" style={{ position: "absolute", top: 4, left: 4, fontSize: 10 }}>cover</span> : null}
                <button
                  className="btn btn-sm btn-danger"
                  style={{ position: "absolute", top: 2, right: 2, padding: "0 6px", lineHeight: 1.4 }}
                  title="Remove from this product"
                  onClick={() => {
                    // Destructive and it cascades: the image also leaves every
                    // variant's selection. Worth one confirm.
                    if (window.confirm("Remove this image from the product? It will also be removed from any variant that shows it.")) {
                      dispatch(deleteProductMedia(product.id, m.id));
                    }
                  }}>×</button>
              </div>
            ))}
          </div>
        )}
        <div className="small text-inverse text-opacity-50 mt-2">
          Drag to reorder. The first image is used as the product thumbnail. Removing an image here
          deletes it from the product, not from storage.
        </div>
      </CardBody>
    </Card>
  );
}

// -------------------------------------------------------- per-variant image pick

function VariantMediaPicker({ product, variant, onClose }) {
  const dispatch = useDispatch();
  const [selected, setSelected] = useState(variant.mediaIds || []);
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  return (
    <div className="border rounded p-2 mt-2" style={{ borderColor: "rgba(255,255,255,.15)" }}>
      <div className="small text-inverse text-opacity-75 mb-2">
        Images shown for <strong>{variant.name || variant.sku}</strong>. Click to select; the order you pick is the
        order they display. Select none to fall back to all product images.
      </div>
      <div className="d-flex flex-wrap gap-2 mb-2">
        {(product.media || []).map((m) => {
          const idx = selected.indexOf(m.id);
          return (
            <div key={m.id} role="button" onClick={() => toggle(m.id)} style={{ position: "relative" }}>
              <img src={m.url} alt="" width="64" height="64"
                style={{
                  objectFit: "cover", borderRadius: 6,
                  border: idx >= 0 ? "2px solid var(--bs-teal, #20c997)" : "1px solid rgba(255,255,255,.15)",
                  opacity: idx >= 0 ? 1 : 0.55,
                }} />
              {idx >= 0 ? (
                <span className="badge bg-theme" style={{ position: "absolute", top: 2, right: 2, fontSize: 10 }}>{idx + 1}</span>
              ) : null}
            </div>
          );
        })}
      </div>
      <button className="btn btn-sm btn-theme me-2"
        onClick={async () => { await dispatch(setVariantMedia(product.id, variant.id, selected)); onClose(); }}>
        Save selection
      </button>
      <button className="btn btn-sm btn-outline-theme" onClick={onClose}>Cancel</button>
    </div>
  );
}

// ------------------------------------------------------------- variants matrix

// One row per variant, two columns per channel (Available + Price) exactly as the
// Saleor grid presents it, plus stock.
// Maps channel slug to the region key used in original_price_<region>_<color> metadata.
const CHANNEL_REGION = { 'default-channel': 'us', 'barsys-india-web': 'india', 'barsys-uk-website': 'uk' };
// Derives color from SKU suffix (e.g. barsys-360-black -> black).
function skuColor(sku) { const p = (sku || '').split('-'); return p[p.length - 1] || 'black'; }

function VariantMatrix({ product, channels, draft, setDraft, failures }) {
  const [picker, setPicker] = useState(null);
  const failed = (key) => failures.find((f) => f.key === key);

  const setPrice = (variantId, channel, value) =>
    setDraft((d) => ({ ...d, prices: { ...d.prices, [`${variantId}:${channel}`]: value } }));
  const setAvail = (variantId, channel, value) =>
    setDraft((d) => ({ ...d, availability: { ...d.availability, [`${variantId}:${channel}`]: value } }));
  const setStock = (variantId, value) =>
    setDraft((d) => ({ ...d, stock: { ...d.stock, [variantId]: value } }));
  const setStrikePrice = (variantId, channel, value) =>
    setDraft((d) => ({ ...d, strikePrices: { ...d.strikePrices, [`${variantId}:${channel}`]: value } }));

  return (
    <Card className="mb-3">
      <CardHeader>Variants</CardHeader>
      <CardBody className="p-0">
        <div className="table-responsive">
          <table className="table table-sm align-middle mb-0">
            <thead>
              <tr>
                <th rowSpan="2" className="ps-3">Variant</th>
                <th rowSpan="2">SKU</th>
                {channels.map((c) => <th key={c.slug} colSpan="3" className="text-center border-start fw-semibold" style={{ borderBottom: "2px solid var(--bs-border-color)" }}>{c.name}</th>)}
                <th rowSpan="2" className="border-start">Stock</th>
                <th rowSpan="2"></th>
              </tr>
              <tr>
                {channels.map((c) => (
                  <React.Fragment key={c.slug}>
                    <th className="small text-inverse text-opacity-50 border-start">Available</th>
                    <th className="small text-inverse text-opacity-50">Price</th>
                    <th className="small text-inverse text-opacity-50">Strike</th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {(product.variants || []).map((v) => (
                <React.Fragment key={v.id}>
                  <tr>
                    <td className="ps-3">{v.name || <span className="text-inverse text-opacity-50">unnamed</span>}</td>
                    <td className="small text-inverse text-opacity-75">{v.sku}</td>
                    {channels.map((c) => {
                      const live = (v.channels || []).find((x) => x.channel === c.slug) || { available: false, amount: null, currency: c.currency };
                      const pKey = `${v.id}:${c.slug}`;
                      const priceVal = draft.prices[pKey] != null ? draft.prices[pKey] : (live.amount == null ? "" : String(live.amount));
                      const availVal = draft.availability[pKey] != null ? draft.availability[pKey] : live.available;
                      const pFail = failed(`price:${v.id}:${c.slug}`);
                      const aFail = failed(`available:${v.id}:${c.slug}`);
                      return (
                        <React.Fragment key={c.slug}>
                          <td className="text-center border-start">
                            <input type="checkbox" className="form-check-input" checked={!!availVal}
                              title={aFail ? aFail.message : undefined}
                              style={aFail ? { outline: "2px solid var(--bs-danger)" } : undefined}
                              onChange={(e) => setAvail(v.id, c.slug, e.target.checked)} />
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-1">
                              <span className="small text-inverse text-opacity-50">{live.currency}</span>
                              <input className="form-control form-control-sm" style={{ width: 80, ...(pFail ? { borderColor: "var(--bs-danger)" } : {}) }}
                                inputMode="decimal" placeholder="—" value={priceVal}
                                title={pFail ? pFail.message : undefined}
                                onChange={(e) => setPrice(v.id, c.slug, e.target.value)} />
                            </div>
                            {aFail ? <div className="small text-danger">{aFail.message}</div> : null}
                          </td>
                          <td>
                            {(() => {
                              const region = CHANNEL_REGION[c.slug];
                              if (!region) return <span className="small text-inverse text-opacity-25">—</span>;
                              const color = skuColor(v.sku);
                              const metaKey = `original_price_${region}_${color}`;
                              const sKey = `${v.id}:${c.slug}`;
                              const metaVal = (product.metadata || []).find((m) => m.key === metaKey);
                              const strikeVal = draft.strikePrices[sKey] != null ? draft.strikePrices[sKey] : (metaVal ? metaVal.value : '');
                              return (
                                <div className="d-flex align-items-center gap-1">
                                  <span className="small text-inverse text-opacity-50">{live.currency}</span>
                                  <input className="form-control form-control-sm" style={{ width: 80 }}
                                    inputMode="decimal" placeholder="—" value={strikeVal}
                                    title="Strikethrough (was) price shown on website"
                                    onChange={(e) => setStrikePrice(v.id, c.slug, e.target.value)} />
                                </div>
                              );
                            })()}
                          </td>
                        </React.Fragment>
                      );
                    })}
                    <td className="border-start">
                      <input className="form-control form-control-sm" style={{ width: 80 }} inputMode="numeric"
                        value={draft.stock[v.id] != null ? draft.stock[v.id] : String(v.stock ? v.stock.quantity : 0)}
                        onChange={(e) => setStock(v.id, e.target.value)} />
                      <div className="small text-inverse text-opacity-50">avail {v.stock ? v.stock.available : 0}</div>
                    </td>
                    <td>
                      <button className="btn btn-sm btn-outline-theme"
                        onClick={() => setPicker(picker === v.id ? null : v.id)}>Images</button>
                    </td>
                  </tr>
                  {picker === v.id ? (
                    <tr><td colSpan={3 + channels.length * 3 + 1}>
                      <VariantMediaPicker product={product} variant={v} onClose={() => setPicker(null)} />
                    </td></tr>
                  ) : null}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </CardBody>
    </Card>
  );
}

// --------------------------------------------------------------------- the page

const emptyDraft = { prices: {}, availability: {}, stock: {}, strikePrices: {}, product: null };

export default function AdminProductDetail() {
  const { productId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    adminProduct: product, adminProductLoading, adminProductError,
    adminProductSaving, adminProductSaveFailures, adminSaveError,
  } = useSelector((s) => s.commerceAdmin);

  const [draft, setDraft] = useState(emptyDraft);
  const [banner, setBanner] = useState(null);

  useEffect(() => { dispatch(fetchAdminProduct(productId)); }, [dispatch, productId]);
  // A fresh product means any half-typed edit belongs to the previous one.
  useEffect(() => { setDraft(emptyDraft); }, [productId]);

  const channels = useMemo(() => {
    const seen = new Map();
    for (const v of (product && product.variants) || []) {
      for (const c of v.channels || []) if (!seen.has(c.channel)) seen.set(c.channel, c);
    }
    return [...seen.values()].map((c) => ({ slug: c.channel, name: c.name || c.channel, currency: c.currency }));
  }, [product]);

  if (adminProductLoading && !product) return <div className="text-center py-5 text-inverse text-opacity-50">Loading…</div>;
  if (adminProductError) return <div className="alert alert-danger">Could not load product: {adminProductError}</div>;
  if (!product) return null;

  const field = (name) => (draft.product && draft.product[name] != null ? draft.product[name] : product[name] || "");
  const setField = (name, value) => setDraft((d) => ({ ...d, product: { ...(d.product || {}), [name]: value } }));
  // Read the document out of the DRAFT (field() falls back to the loaded product),
  // so an edit round-trips through the same string the save sends. That reuses the
  // existing dirty check and the failed-changes retention keyed on "product".
  const structuredDoc = parseDoc(field("description"));
  // JSON.stringify, not serialize(): serialize() drops emptied paragraphs, and doing
  // that on every keystroke would delete a block the moment someone cleared it to
  // retype. Emptied blocks are dropped once, at save.
  const setDoc = (doc) => setField("description", JSON.stringify(doc));

  const dirty = draft.product
    || Object.keys(draft.prices || {}).length || Object.keys(draft.availability || {}).length || Object.keys(draft.stock || {}).length || Object.keys(draft.strikePrices || {}).length;

  const onSave = async () => {
    setBanner(null);
    const changes = { product: null, prices: [], availability: [], stock: [] };
    if (draft.product) {
      changes.product = {
        slug: field("slug"), name: field("name"),
        // A structured description is re-serialized from its blocks, which keeps
        // ids, types, extra data keys and unknown top-level fields intact (see
        // ./editorJsDoc). It cannot be omitted: adminProductUpsert writes
        // `description=$3` unconditionally with `description || null`, so leaving
        // the field out would store NULL and wipe the copy.
        description: structuredDoc ? serialize(structuredDoc) : field("description"),
        isPublished: draft.product.isPublished != null ? draft.product.isPublished : product.isPublished,
      };
    }
    for (const [k, v] of Object.entries(draft.prices)) {
      const [variantId, channel] = k.split(":");
      const amount = parseFloat(v);
      if (!Number.isNaN(amount)) changes.prices.push({ variantId: Number(variantId), channel, amount });
    }
    for (const [k, v] of Object.entries(draft.availability)) {
      const [variantId, channel] = k.split(":");
      changes.availability.push({ variantId: Number(variantId), channel, available: !!v });
    }
    for (const [variantId, q] of Object.entries(draft.stock)) {
      const quantity = parseInt(q, 10);
      if (!Number.isNaN(quantity)) changes.stock.push({ variantId: Number(variantId), quantity });
    }

    // Convert strike prices into product metadata entries (original_price_<region>_<color>).
    const strikeMeta = [];
    for (const [k, v] of Object.entries(draft.strikePrices)) {
      const [variantId, channel] = k.split(':');
      const region = CHANNEL_REGION[channel];
      if (!region) continue;
      const variant = (product.variants || []).find((vr) => String(vr.id) === variantId);
      if (!variant) continue;
      const color = skuColor(variant.sku);
      strikeMeta.push({ key: `original_price_${region}_${color}`, value: String(v) });
    }
    if (strikeMeta.length > 0) {
      if (!changes.product) {
        changes.product = {
          slug: product.slug, name: product.name,
          description: product.description || '',
          isPublished: product.isPublished,
        };
      }
      changes.product.metadata = strikeMeta;
    }

    const res = await dispatch(saveProductForm(product.id, changes));
    if (res && res.ok) {
      setDraft({ ...emptyDraft, strikePrices: {} });
      setBanner({ kind: "success", text: "Saved." });
    } else {
      // Keep the failed edits in the draft so they stay on screen and stay
      // editable — clearing them would discard the operator's work and leave the
      // form looking saved.
      const kept = new Set((res.failures || []).map((f) => f.key));
      setDraft((d) => ({
        product: kept.has("product") ? d.product : null,
        prices: Object.fromEntries(Object.entries(d.prices).filter(([k]) => kept.has(`price:${k}`))),
        availability: Object.fromEntries(Object.entries(d.availability).filter(([k]) => kept.has(`available:${k}`))),
        stock: Object.fromEntries(Object.entries(d.stock).filter(([k]) => kept.has(`stock:${k}`))),
        strikePrices: d.strikePrices || {},
      }));
      setBanner({
        kind: "warning",
        text: `${(res.failures || []).length} change${res.failures.length === 1 ? "" : "s"} failed and ${
          res.results.length - res.failures.length} saved. The failed fields are still highlighted below.`,
      });
    }
  };

  return (
    <div>
      <div className="d-flex align-items-center gap-2 mb-3">
        <button className="btn btn-outline-theme btn-sm" onClick={() => navigate("/orders/products")}>←</button>
        <div className="flex-grow-1">
          <h1 className="page-header mb-0">{product.name}</h1>
          <div className="text-inverse text-opacity-50 small">/{product.slug}{product.productType ? ` · ${product.productType}` : ""}</div>
        </div>
        <button className="btn btn-theme" disabled={!dirty || adminProductSaving} onClick={onSave}>
          {adminProductSaving ? "Saving…" : "Save"}
        </button>
      </div>

      {banner ? <div className={`alert alert-${banner.kind === "success" ? "success" : "warning"}`}>{banner.text}</div> : null}
      {adminSaveError ? <div className="alert alert-warning">{adminSaveError}</div> : null}

      <div className="row">
        <div className="col-xl-8">
          <Card className="mb-3">
            <CardHeader>General Information</CardHeader>
            <CardBody>
              <label className="form-label small text-inverse text-opacity-50">Name</label>
              <input className="form-control mb-3" value={field("name")} onChange={(e) => setField("name", e.target.value)} />
              <label className="form-label small text-inverse text-opacity-50">Slug</label>
              <input className="form-control mb-3" value={field("slug")} onChange={(e) => setField("slug", e.target.value)} />
              <label className="form-label small text-inverse text-opacity-50">Category</label>
              <div className="mb-3"><CategoryPicker productId={product.id} /></div>
              <label className="form-label small text-inverse text-opacity-50">Description</label>
              {structuredDoc ? (
                <>
                  <div className="small text-inverse text-opacity-50 mb-2">
                    A formatted document the storefront renders — {structuredDoc.blocks.length} block
                    {structuredDoc.blocks.length === 1 ? "" : "s"}, edited one at a time so the structure survives.
                  </div>
                  <DescriptionEditor doc={structuredDoc} onChange={setDoc} />
                </>
              ) : (
                <textarea className="form-control" rows="6" value={field("description")} onChange={(e) => setField("description", e.target.value)} />
              )}
            </CardBody>
          </Card>

          <PDPMediaOrder product={product} />
          <MediaManager product={product} />
        </div>

        <div className="col-xl-4">
          <Card className="mb-3">
            <CardHeader>Availability</CardHeader>
            <CardBody>
              <label className="form-check form-switch mb-3">
                <input className="form-check-input" type="checkbox"
                  checked={draft.product && draft.product.isPublished != null ? draft.product.isPublished : product.isPublished}
                  onChange={(e) => setField("isPublished", e.target.checked)} />
                <span className="form-check-label">{(draft.product && draft.product.isPublished != null ? draft.product.isPublished : product.isPublished) ? "Published" : "Draft"}</span>
              </label>
              {channels.map((c) => {
                const live = (product.variants || []).filter((v) => (v.channels || []).some((x) => x.channel === c.slug && x.available)).length;
                return (
                  <div key={c.slug} className="d-flex justify-content-between align-items-center py-1">
                    <span className="small">{c.name}</span>
                    <span className="small text-inverse text-opacity-50">
                      {live} of {(product.variants || []).length} variants · {c.currency}
                    </span>
                  </div>
                );
              })}
              <div className="small text-inverse text-opacity-50 mt-2">
                Per-channel selling is set on each variant in the matrix. Publishing controls whether the
                product is visible at all.
              </div>
            </CardBody>
          </Card>

          {(product.metadata || []).length ? (
            <Card>
              <CardHeader>Metadata</CardHeader>
              <CardBody>
                {product.metadata.map((m) => (
                  <div key={m.key} className="d-flex justify-content-between small py-1">
                    <span className="text-inverse text-opacity-50">{m.key}</span>
                    <span>{m.value}</span>
                  </div>
                ))}
                <div className="small text-inverse text-opacity-50 mt-2">Read-only — no write path exists yet.</div>
              </CardBody>
            </Card>
          ) : null}
        </div>
      </div>

      <VariantMatrix
        product={product}
        channels={channels}
        draft={draft}
        setDraft={setDraft}
        failures={adminProductSaveFailures || []}
      />
    </div>
  );
}
